import type { SpreadsheetId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import type { TranslationFnc } from '~/_shared/utils/hooks';
import { BOUNDARY_GROUP_TIE_ID } from '~/boundary/settings/boundarySettings.helpers';
import { copy } from '../../_shared/utils/collections/collections';
import type { BoundaryId } from '../boundaries/boundaryIdentifier.type';
import { BoundaryTerritoryType } from '../boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import type { BoundaryTerritoryGroup } from '../boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import type { BoundaryTerritoryGroupDetailsRequestQueryModel } from './boundaryTerritoryDetails.repository';
import type { BoundaryTerritoryCountsAndExtraData } from './boundaryTerritoryDetails.selectors';
import {
  type BoundaryTerritoryDetailsState, type BoundaryTerritoryGroupDetailsItem,
} from './boundaryTerritoryGroups.state';

export const assignBoundaryToTerritory = (
  state: BoundaryTerritoryDetailsState, boundaryTerritoryGroupId: number, territoryId: string, boundaryId: number
): BoundaryTerritoryDetailsState => {
  const details = state.territoryDetails.data.get(boundaryTerritoryGroupId);
  if (!details) {
    return state;
  }

  const manualAssignmentsClone = { ...details.manualAssignments };
  manualAssignmentsClone[territoryId] = [...(manualAssignmentsClone[territoryId] ?? []), boundaryId];
  const updatedDetails: BoundaryTerritoryGroupDetailsItem = { ...details, manualAssignments: manualAssignmentsClone };

  const updatedTerritoryGroupDetails = copy.andReplace(state.territoryDetails.data, boundaryTerritoryGroupId, updatedDetails);
  const stateClone = { ...state };

  stateClone.territoryDetails.data = updatedTerritoryGroupDetails;
  return stateClone;
};

export const removeTerritoryAssignmentsFromBoundaryTerritoryDetails = (
  state: BoundaryTerritoryDetailsState, boundaryTerritoryGroupId: number, territoryId: string
): BoundaryTerritoryDetailsState => {
  const currentDetails = state.territoryDetails.data.get(boundaryTerritoryGroupId);

  if (!currentDetails) {
    return state;
  }

  const newAssignments = {
    ...currentDetails.assignments,
  };
  delete newAssignments[territoryId];

  const newManualAssignments = {
    ...currentDetails.manualAssignments,
  };
  delete newManualAssignments[territoryId];

  const newDetails: BoundaryTerritoryGroupDetailsItem = {
    ...currentDetails,
    assignments: newAssignments,
    manualAssignments: newManualAssignments,
  };

  return {
    ...state,
    territoryDetails: {
      ...state.territoryDetails,
      data: copy.andReplace(state.territoryDetails.data, boundaryTerritoryGroupId, newDetails),
    },
  };
};

export const getBtgExtraDataQuery = (boundaryTerritoryGroup: BoundaryTerritoryGroup, spreadsheetId: SpreadsheetId): BoundaryTerritoryGroupDetailsRequestQueryModel['extraData'] => {
  const { settings } = boundaryTerritoryGroup;
  const {
    boundaryTerritoryType, demographicId, bucketColumnId, calculateBucketFunction, labelValueConfig: {
      isValueCustom,
      labelValueType,
      labelValueColumnId,
      labelValueDemographicId,
      labelValueAggregationFunction,
    },
  } = settings;

  const selectedBoundaryTerritoryType = isValueCustom ? labelValueType : boundaryTerritoryType;
  const selectedDemographic = isValueCustom ? labelValueDemographicId : demographicId;
  const selectedCalculateBucketFunction = isValueCustom ? labelValueAggregationFunction : calculateBucketFunction;
  const selectedLabelValueColumnId = isValueCustom ? labelValueColumnId : bucketColumnId;

  // If boundaryTerritoryType is "manual" or "density", do nothing
  if ((selectedBoundaryTerritoryType === 'manual') || (selectedBoundaryTerritoryType === 'density')) {
    return undefined;
  }

  // If demographics, create demographics query
  if ((selectedBoundaryTerritoryType === 'demographic') && selectedDemographic) {
    return [{
      type: 'Demographic',
      demographicId: selectedDemographic,
    }];
  }

  // Otherwise use bucketColumnId and calculateBucketFunction
  if (selectedLabelValueColumnId && selectedCalculateBucketFunction) {
    return [{
      type: 'SpreadsheetColumn',
      function: selectedCalculateBucketFunction,
      column: {
        spreadsheetId,
        columnId: selectedLabelValueColumnId,
      },
    }];
  }

  return undefined;
};

/**
 * Gets a callback function for boundary value labels based on boundary territory group settings.
 * Returns either count data for density type or extra data for other types.
 */
export const getBoundaryLabelValueCallback = (
  boundaryTerritoryGroup: BoundaryTerritoryGroup,
  { extraData, counts }: BoundaryTerritoryCountsAndExtraData,
  t: TranslationFnc,
): Maybe<(boundaryId: BoundaryId) => string | null> => {
  const { settings } = boundaryTerritoryGroup;
  const {
    boundaryTerritoryType, demographicId, bucketColumnId, calculateBucketFunction, labelValueConfig: {
      isValueCustom,
      labelValueType,
      labelValueColumnId,
      labelValueDemographicId,
      labelValueAggregationFunction,
    },
  } = settings;

  const selectedBoundaryTerritoryType = isValueCustom ? labelValueType : boundaryTerritoryType;
  const selectedDemographic = isValueCustom ? labelValueDemographicId : demographicId;
  const selectedCalculateBucketFunction = isValueCustom ? labelValueAggregationFunction : calculateBucketFunction;
  const selectedLabelValueColumnId = isValueCustom ? labelValueColumnId : bucketColumnId;

  // For manual type, return null to use boundary.displayName
  if (selectedBoundaryTerritoryType === BoundaryTerritoryType.Manual) {
    return null;
  }

  // For density type, use count data
  if (selectedBoundaryTerritoryType === BoundaryTerritoryType.Density) {
    return (boundaryId: BoundaryId) => {
      const count = counts?.[boundaryId];
      return (count ?? 0).toString();
    };
  }

  // For other types (demographic, numeric, groups), use extra data
  if (extraData?.length) {
    let targetExtraData: typeof extraData[0] | undefined;

    // For demographic type, find the demographic entry
    if ((selectedBoundaryTerritoryType === BoundaryTerritoryType.Demographic) && selectedDemographic) {
      targetExtraData = extraData.find(item =>
        (item.type === 'Demographic') && (item.demographicId === selectedDemographic)
      );
    }
    // For other types (numeric, groups), find the spreadsheet column entry
    else if (selectedLabelValueColumnId && selectedCalculateBucketFunction) {
      targetExtraData = extraData.find(item =>
        (item.type === 'SpreadsheetColumn') &&
        (item.function === selectedCalculateBucketFunction) &&
        (item.column.columnId === selectedLabelValueColumnId)
      );
    }

    if (targetExtraData) {
      return (boundaryId: BoundaryId) => {
        const boundaryData = targetExtraData.data[boundaryId];
        if (boundaryData?.value === BOUNDARY_GROUP_TIE_ID) {
          return t('boundary-territory-name-group-tie');
        }
        return boundaryData?.formattedValue ?? null;
      };
    }
  }

  return null;
};
